// features/admin/setCreditApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { adminrefreshToken } from '../../utils/adminRefreshToken.ts';

export interface UserCreditDetail {
  credit_limit: number;
  credit_balance: number;
}

export interface OrderDetail {
  order_id: number;
  invoice_no: string;
  total_order_amount: number;
  order_status: string;
  pay_later: boolean | number;
}

export interface RepaymentDetail {
  repayment_id: number;
  order_id: number;
  amount: number;
  stripe_session_id: string;
  stripe_payment_intent: string;
  status: string;
  repayment_date: string;
}

export interface CreditUserResponse {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  credit: UserCreditDetail;
  orders: OrderDetail[];
  repayments: RepaymentDetail[];
}

export interface CreditApiResponse<T> {
  error: boolean;
  message: string;
  count?: number;
  result?: T;
  data?: T;
  total?: number;
}

export interface SetCreditRequest {
  user_id: number;
  credit_limit: number;
  balance?: number;
  // New Pay Later API field: number of days before auto-debit.
  credit_period_days?: number;
}

export interface UpdateCreditRequest {
  user_id: number;
  credit_limit: number;
  balance?: number;
  // New Pay Later API field: number of days before auto-debit.
  credit_period_days?: number;
}

export interface DeleteCreditRequest {
  user_id: number;
}

export interface CreditUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  credit_limit: number;
  balance: number;
  orders_count?: number;
  repayments_count?: number;
  created_at?: string;
  updated_at?: string;
  credit_period_days?: number;
}

export interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  status?: string;
  member_type?: string;
  subscription_date?: string | null;
  expiry_date?: string | null;
}

export interface SearchUserRequest {
  search?: string;
  limit?: number;
}

export interface SearchUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

// ── NEW interface for per-user credit details ──
export interface UserCreditDetailsData {
  user_id: number;
  credit_limit: number;
  balance: number;
  orders: {
    order_id: number;
    invoice_no: string;
    total_order_amount: number;
    order_status: string;
    order_date: string;
    pay_later: number;
  }[];
  credit_period_days?: number;
  auto_debit_card_saved?: boolean;
  repayments: {
    order_id: number;
    invoice_no: string;
    repayment_amount: number;
    repayment_status: string;
    repayment_date: string;
  }[];
}

export const CREDIT_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT,
  ENDPOINTS: {
    SET_CREDIT:               '/admin/setCredit',
    LIST_CREDIT_USERS:        '/admin/CreditlistUsers',
    UPDATE_CREDIT:            '/admin/setCredit',
    DELETE_CREDIT:            '/admin/deleteCredit',
    GET_USER_CREDIT:          '/admin/getUserCredit',
    GET_USER_CREDIT_DETAILS:  '/admin/getUserCreditDetails', // ── NEW
    SEARCH_USERS:             '/admin/searchUsers',
    LIST_USERS:               '/admin/listUsers',
  },
};

export const creditSalesApi = createApi({
  reducerPath: 'setCreditApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CREDIT_API_CONFIG.BASE_URL,
    prepareHeaders: async (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['UserCredit', 'CreditUsers', 'SearchUsers', 'AllUsers', 'UserCreditDetails'],
  endpoints: (build) => ({

    setCredit: build.mutation<CreditApiResponse<any>, SetCreditRequest>({
      query: (data) => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.SET_CREDIT,
        method: 'POST',
        body: {
          user_id: data.user_id,
          credit_limit: data.credit_limit,
          balance: data.balance ?? data.credit_limit,
          ...(data.credit_period_days !== undefined ? { credit_period_days: data.credit_period_days } : {}),
        },
      }),
      invalidatesTags: ['CreditUsers', 'AllUsers'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || 'Credit set successfully',
        result: response.result || response.data,
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to set credit',
        status: response.status,
      }),
    }),

    listCreditUsers: build.query<CreditApiResponse<CreditUser[]>, void>({
      query: () => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.LIST_CREDIT_USERS,
        method: 'POST',
        body: { user_id: parseInt(localStorage.getItem('admin_user_id') || '110') },
      }),
      providesTags: ['CreditUsers'],
      transformResponse: (response: any) => {
        if (!response || response.error) {
          return { error: true, message: response?.message || 'No data received', result: [], data: [] };
        }
        const users: CreditUser[] = [];
        if (response.data && Array.isArray(response.data)) {
          for (const backendUser of response.data) {
            users.push({
              user_id:          backendUser.user_id,
              first_name:       backendUser.first_name || '',
              last_name:        backendUser.last_name  || '',
              email:            backendUser.user_email    || '',
              phone:            backendUser.user_contact  || '',
              credit_limit:     backendUser.credit?.credit_limit   || 0,
              balance:          backendUser.credit?.credit_balance  || 0,
              orders_count:     backendUser.orders?.length     || 0,
              repayments_count: backendUser.repayments?.length || 0,
              credit_period_days: backendUser.credit_period_days ?? backendUser.credit?.credit_period_days,
            });
          }
        }
        return {
          error:   response.error   || false,
          message: response.message || '',
          count:   response.count   || users.length,
          result:  users,
          data:    users,
        };
      },
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to load credit users',
        status: response.status,
      }),
    }),

    listAllUsers: build.query<CreditApiResponse<User[]>, { filter_type?: number }>({
      query: (params) => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.LIST_USERS,
        method: 'POST',
        body: { filter_type: params?.filter_type ?? 0 },
      }),
      providesTags: ['AllUsers'],
      transformResponse: (response: any) => {
        let users: User[] = [];
        if (response?.error === false && response?.data && Array.isArray(response.data)) users = response.data;
        else if (Array.isArray(response)) users = response;
        else if (response?.data  && Array.isArray(response.data))  users = response.data;
        else if (response?.users && Array.isArray(response.users)) users = response.users;

        const transformedUsers: User[] = users.map((user: any) => ({
          user_id:           user.user_id    || user.id    || 0,
          first_name:        user.first_name || user.firstName || '',
          last_name:         user.last_name  || user.lastName  || '',
          user_email:        user.user_email || user.email     || '',
          user_contact:      user.user_contact || user.contact || user.phone || '',
          status:            user.status       || 'active',
          member_type:       user.member_type  || user.membership_type || 'none',
          subscription_date: user.subscription_date || null,
          expiry_date:       user.expiry_date       || null,
        }));

        return {
          error:   response?.error   || false,
          message: response?.message || 'Users loaded successfully',
          total:   response?.total   || response?.count || transformedUsers.length,
          result:  transformedUsers,
          data:    transformedUsers,
        };
      },
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to load users',
        data: [], result: [],
      }),
    }),

    updateCredit: build.mutation<CreditApiResponse<any>, UpdateCreditRequest>({
      query: (data) => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.UPDATE_CREDIT,
        method: 'POST',
        body: {
          user_id: data.user_id,
          credit_limit: data.credit_limit,
          balance: data.balance ?? data.credit_limit,
          ...(data.credit_period_days !== undefined ? { credit_period_days: data.credit_period_days } : {}),
        },
      }),
      invalidatesTags: ['CreditUsers'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || 'Credit updated successfully',
        result: response.result || response.data,
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to update credit',
        status: response.status,
      }),
    }),

    deleteCredit: build.mutation<CreditApiResponse<any>, DeleteCreditRequest>({
      query: (data) => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.DELETE_CREDIT,
        method: 'POST',
        body: { user_id: data.user_id },
      }),
      invalidatesTags: ['CreditUsers', 'AllUsers'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || 'Credit facility removed successfully',
        result: response.result || response.data,
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to delete credit',
        status: response.status,
      }),
    }),

    getUserCredit: build.query<CreditApiResponse<any>, { user_id: number }>({
      query: ({ user_id }) => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.GET_USER_CREDIT,
        method: 'POST',
        body: { user_id },
      }),
      providesTags: ['UserCredit'],
      transformResponse: (response: any) => ({
        error:   response.error   || false,
        message: response.message || '',
        result:  response.result  || response.data,
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to get user credit',
        status: response.status,
      }),
    }),

    // ── NEW: per-user credit details for admin transaction dialog ──
    getUserCreditDetails: build.query<CreditApiResponse<UserCreditDetailsData>, { user_id: number }>({
      query: ({ user_id }) => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.GET_USER_CREDIT_DETAILS,
        method: 'POST',
        body: { user_id },
      }),
      providesTags: (_result, _error, { user_id }) => [{ type: 'UserCreditDetails', id: user_id }],
      transformResponse: (response: any) => ({
        error:   response.error   || false,
        message: response.message || '',
        data:    response.data,
        result:  response.data,
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to fetch credit details',
        status: response.status,
      }),
    }),

    searchUsers: build.query<CreditApiResponse<SearchUser[]>, SearchUserRequest>({
      query: (params) => ({
        url: CREDIT_API_CONFIG.ENDPOINTS.SEARCH_USERS,
        method: 'POST',
        body: { search: params.search || '', limit: params.limit || 20 },
      }),
      providesTags: ['SearchUsers'],
      transformResponse: (response: any) => {
        let usersData: SearchUser[] = [];
        if (response?.data && Array.isArray(response.data)) {
          usersData = response.data.map((user: any) => ({
            user_id:    user.id    || user.user_id,
            first_name: user.first_name || '',
            last_name:  user.last_name  || '',
            email:      user.email      || user.user_email    || '',
            phone:      user.phone      || user.user_contact  || '',
          }));
        }
        return {
          error:   response?.error   || false,
          message: response?.message || '',
          result:  usersData,
          data:    usersData,
        };
      },
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to search users',
        status: response.status,
      }),
    }),
  }),
});

export const {
  useSetCreditMutation,
  useListCreditUsersQuery,
  useListAllUsersQuery,
  useUpdateCreditMutation,
  useDeleteCreditMutation,
  useGetUserCreditQuery,
  useGetUserCreditDetailsQuery, // ── NEW
  useSearchUsersQuery,
} = creditSalesApi;